import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import DocumentConfirmModal from "./DocumentConfirmModal";
import Handler from "../../../Helpers/Handler";
import { formatDate, textCapitalize } from "../../../Helpers/Utils";

const STATUS_TONE = { draft: "text-bg-secondary", pending: "text-bg-warning", published: "text-bg-success" };
const STATUS_LABEL = { draft: "Draft", pending: "Pending", published: "Published" };

const DocumentHistoryModal = ({ documentId, documentName, onClose, onChanged }) => {
    const modalRef = useRef(null);
    const changedRef = useRef(false);
    const [data, setData] = useState({ list: [], latest: null, live: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(null);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el, { backdrop: "static" });
        const handleHidden = () => { if (changedRef.current) onChanged?.(); onClose?.(); };
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.dispose();
            document.querySelectorAll(".modal-backdrop").forEach((node) => node.remove());
            document.body.classList.remove("modal-open");
            document.body.style.removeProperty("overflow");
            document.body.style.removeProperty("padding-right");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openVersion = useCallback(async (version) => {
        setSelected(version);
        setPreview(null);
        setPreviewLoading(true);
        const response = await Handler({ method: "get", url: `/admin/document/${documentId}/versions/${version}` });
        if (response?.success) setPreview(response?.data?.data || null);
        else toast.error(textCapitalize(response?.data?.message || "Unable to load this version"));
        setPreviewLoading(false);
    }, [documentId]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({ method: "get", url: `/admin/document/${documentId}/versions` });
        if (response?.success) {
            const result = response?.data?.data || { list: [], latest: null, live: null };
            setData(result);
            const first = result.live || result.latest || result.list?.[0]?.version;
            if (first) openVersion(first);
        } else {
            setError(response?.data?.message || "Unable to load the history");
        }
        setLoading(false);
    }, [documentId, openVersion]);

    useEffect(() => { load(); }, [load]);

    const askAct = (kind) => { if (selected) setConfirm({ kind, version: selected }); };

    const runAct = async () => {
        if (!confirm) return;
        const publish = confirm.kind === "publish";
        setBusy(true);
        const response = await Handler({ method: "post", url: `/admin/document/${documentId}/versions/${confirm.version}/${publish ? "publish" : "restore"}` });
        setBusy(false);
        setConfirm(null);
        if (response?.success) {
            toast.success(response?.data?.message || "Done");
            changedRef.current = true;
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Something went wrong"));
        }
    };

    const rows = data.list || [];
    const selectedRow = rows.find((r) => r.version === selected);

    return createPortal(
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">History{documentName ? ` · ${documentName}` : ""}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {loading ? <div className="text-muted py-5 text-center">Loading...</div> : error ? (
                            <div className="text-center text-muted py-5">
                                <div className="mb-2">{error}</div>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>Retry</button>
                            </div>
                        ) : (
                            <div className="row g-3">
                                <div className="col-12 col-md-4">
                                    <div className="list-group">
                                        {rows.map((r) => (
                                            <button key={r.version} type="button" className={`list-group-item list-group-item-action${selected === r.version ? " active" : ""}`} onClick={() => openVersion(r.version)}>
                                                <div className="d-flex justify-content-between align-items-center gap-2">
                                                    <span className="fw-semibold">Version {r.version}{r.version === data.latest ? " · latest" : ""}</span>
                                                    <span className={`badge ${STATUS_TONE[r.status] || STATUS_TONE.draft}`}>{STATUS_LABEL[r.status] || "Draft"}</span>
                                                </div>
                                                <div className="small opacity-75">{r.createdAt ? formatDate(r.createdAt) : "-"}</div>
                                                {r.changeNote ? <div className="small mt-1">{r.changeNote}</div> : null}
                                            </button>
                                        ))}
                                    </div>
                                    {rows.length < 2 ? <div className="text-muted small mt-2">Each change to the document text is saved here as a new draft version.</div> : null}
                                </div>
                                <div className="col-12 col-md-8">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="fw-semibold">{selected ? `Version ${selected}` : "Select a version"}</div>
                                        <div className="d-flex gap-2">
                                            {selectedRow && selected !== data.latest ? (
                                                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy || previewLoading} onClick={() => askAct("copy")}>Copy to new draft</button>
                                            ) : null}
                                            {selectedRow && selectedRow.status !== "published" ? (
                                                <button type="button" className="btn btn-sm btn-primary" disabled={busy || previewLoading} onClick={() => askAct("publish")}>{busy ? "Working..." : "Publish this version"}</button>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="border rounded-3 p-3 bg-white" style={{ minHeight: 240, maxHeight: 480, overflow: "auto" }}>
                                        {previewLoading ? <div className="text-muted">Loading...</div> : preview ? (
                                            // Content is written by admins in the editor.
                                            <div dangerouslySetInnerHTML={{ __html: preview.documentUrl || "" }} />
                                        ) : <div className="text-muted">Nothing to show</div>}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
            {confirm ? (
                <DocumentConfirmModal
                    title={confirm.kind === "publish" ? `Publish version ${confirm.version}?` : `Copy version ${confirm.version} to a new draft?`}
                    message={confirm.kind === "publish" ? "This version becomes the live text. The version that is published now will move to Pending." : "Its text is copied into a new draft version. Nothing is overwritten."}
                    confirmLabel={confirm.kind === "publish" ? "Publish" : "Copy to draft"}
                    busy={busy}
                    onConfirm={runAct}
                    onCancel={() => setConfirm(null)}
                />
            ) : null}
        </div>,
        document.body
    );
};

export default DocumentHistoryModal;
